import createClient from "openapi-fetch";
import type { JwtPayload } from "jwt-decode";
import { jwtDecode } from "jwt-decode";
import type { Role, UserInfo, AuthToken, AccessTokenWithLoginOption } from "~/types/entity/user";
import { displayRoleList, validRoleList } from "~/types/entity/user";
import type { MainViewMode } from "~/types/entity/cWebCatalog";
import * as cWebCatalogApi from "~/utils/cWebCatalogApi";
import { isAuthorizedRole, useEntraId2Token } from "~/utils/urlHelper";
import type { components, paths } from "~/types/entity/emerIntwcApiWeb.schema";

/**
 * yyyy-MM-dd 形式の文字列 から年齢を計算する
 * @param birthdate yyyy-MM-dd 形式の文字列
 */
export const getAgeFromBirthDate = (birthDate: Date | undefined) => {
  if (birthDate === undefined) return 0;
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDifference = today.getMonth() - birthDate.getMonth();
  if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

const isAgeOver18 = (birthdate: Date | undefined) => getAgeFromBirthDate(birthdate) >= 18;

export type IUserSnsAuth = components["schemas"]["UserSnsAuth"];

export const useUserModel = () => {
  const state = useUserState();

  const userInfo = () => state.get().value.userInfo;
  const isLoginAttempted = () => (state.get().value.token?.loginAttemptAt ?? 0) > 0;
  const isLogin = () => (state.get().value.token?.accessToken?.length ?? 0) > 0;
  const isAuthorization = () => (state.get().value.userInfo?.roles?.length ?? 0) > 0;
  const canDisplayR18 = () => state.get().value.userInfo?.circlems_r18 === 1 && isAgeOver18(new Date(state.get().value.userInfo?.birthdate ?? ""));
  const displayStatus = () => state.get().value.userInfo?.roles?.find(role => displayRoleList.includes(<Role /* 絞り込み確認のためだけなので cast してよい */>role));
  const clearUserState = () => state.set({ userInfo: convertToUserInfo(undefined), token: undefined });
  const setUserState = (token: AuthToken | undefined, userInfoItem: components["schemas"]["V2UserInfoItem"] | undefined) =>
    state.set({
      token: token,
      userInfo: convertToUserInfo(userInfoItem),
    });

  const getValidAuthToken: () => AuthToken | undefined = () =>
    (state.get().value.token?.expiresAt ?? 0) > Date.now() / 1000
      ? state.get().value.token
      : undefined;

  // ここからトークン取得用 getAccessTokenWithLogin のためのコード
  const xWwwFormUrlencode = (body: unknown) => {
    const urlEncodedData = new URLSearchParams();
    const bodyObject = body as Record<string, unknown>;
    for (const [key, value] of Object.entries(bodyObject)) {
      urlEncodedData.append(key, value as string);
    }
    return urlEncodedData.toString();
  };

  const getAccessTokenWithLogin = async (/* fetchoption: AccessTokenWithLoginOption */): Promise<string | undefined> => {
    // オフライン時に例外が投げられるが認証できなかったものとして扱う
    try {
      return useEntraId2Token
        ? await getAccessTokenWithLoginEntraId2Token()
        : await getAccessTokenWithLoginCookie2Token();
    }
    catch {
      return undefined;
    }
  };

  const msal = useMsal();
  const getAccessTokenWithLoginEntraId2Token = async (): Promise<string | undefined> => {
    {
      // 有効なトークンが userState にあればそれを採用する
      const accessToken = getValidAuthToken()?.accessToken;
      if (accessToken) {
        return accessToken;
      }
    }

    const loginAttemptAt: number = new Date().getTime();
    const authenticationResult = await msal.getActiveAccount();
    /**
     * 認証できなかった時の後処理
     */
    const notAuthenticated = async () => {
      setUserState({
        accessToken: "",
        expiresAt: undefined,
        iat: undefined,
        loginAttemptAt: loginAttemptAt // ログインを試みたことは記録したい
      }, undefined);
      if (authenticationResult?.idToken) {
        await msal.logoutRedirect();
      }
      return undefined;
    };
    if (!authenticationResult?.idToken) {
      return await notAuthenticated();
    }

    // id_token から このアプリケーション用のアクセストークンの取得
    const client = createClient<paths>({
      headers: {
        "X-EntraID-IdToken": authenticationResult.idToken,
        // RFC 6749 OAuth2 における Request は x-www-form-urlencoded
        "Content-Type": "application/x-www-form-urlencoded",
      },
      bodySerializer: xWwwFormUrlencode,
    });
    const response = await client.POST("/api/v2/EntraId2Token", {
      body: {
        scope: "openid",
        grant_type: "client_credentials",
      }
    });
    const data = response.data;
    if (data?.id_token && data?.access_token) {
      const accessToken: string = data.access_token;
      // この /api/v2/UserInfo 呼び出しはトークンの検証機能を兼ねているため setStateAuthToken よりも前に実施する
      const responseUserInfo = await client.POST("/api/v2/UserInfo", {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        }
      });
      // ページから /api/v2/UserInfo を呼ぶ場合については src/composables/view-model/user.ts に setStateUserInfo を記述している
      const userInfoItem = responseUserInfo?.data;
      const jwt: JwtPayload = jwtDecode(data.id_token);
      setUserState({
        ...jwt,
        accessToken: accessToken,
        expiresAt: jwt.exp ?? undefined,
        iat: jwt.iat ?? undefined,
        loginAttemptAt: loginAttemptAt
      }, userInfoItem);
      return accessToken;
    }
    return await notAuthenticated();
  };

  const getAccessTokenWithLoginCookie2Token = async (/* fetchoption: AccessTokenWithLoginOption */): Promise<string | undefined> => {
    // const { getValidAuthToken, setUserState } = fetchoption;
    {
    // 有効なトークンが userState にあればそれを採用する
      const accessToken = getValidAuthToken()?.accessToken;
      if (accessToken) {
        return accessToken;
      }
    }

    // アクセストークンの取得
    const loginAttemptAt: number = new Date().getTime();
    const cookie = useRequestHeader("cookie");
    const client = createClient<paths>({
      headers: {
        "cookie": cookie,
        // RFC 6749 OAuth2 における Request は x-www-form-urlencoded
        "Content-Type": "application/x-www-form-urlencoded",
      },
      bodySerializer: xWwwFormUrlencode,
    });
    const response = await client.POST("/api/v2/Cookie2Token", {
      body: {
        scope: "openid",
        grant_type: "client_credentials",
      }
    });
    const data = response.data;
    if (data?.id_token && data?.access_token) {
      const accessToken: string = data.access_token;
      // この /api/v2/UserInfo 呼び出しはトークンの検証機能を兼ねているため setStateAuthToken よりも前に実施する
      const responseUserInfo = await client.POST("/api/v2/UserInfo", {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        }
      });
      // ページから /api/v2/UserInfo を呼ぶ場合については src/composables/view-model/user.ts に setStateUserInfo を記述している
      const userInfoItem = responseUserInfo?.data;
      const jwt: JwtPayload = jwtDecode(data.id_token);
      setUserState({
        ...jwt,
        accessToken: accessToken,
        expiresAt: jwt.exp ?? undefined,
        iat: jwt.iat ?? undefined,
        loginAttemptAt: loginAttemptAt
      }, userInfoItem);
      return accessToken;
    }
    setUserState({
      accessToken: "",
      expiresAt: undefined,
      iat: undefined,
      loginAttemptAt: loginAttemptAt // ログインを試みたことは記録したい
    }, undefined);
    return undefined;
  };
  const fetchOption: AccessTokenWithLoginOption = { getAccessTokenWithLogin };
  // ここまで トークン取得用コード

  const getUserInfo: () => Promise<UserInfo> = async () => {
    const client = await cWebCatalogApi.createOpenapiFetch(fetchOption);
    const response = await client?.POST("/api/v2/UserInfo");
    const userInfo = convertToUserInfo(response?.data);
    // 自動的なアクセストークン取得に伴い cWebCatalogApi.ts でも /api/v2/UserInfo アクセス後 setUserState を呼び出す
    await state.setUserInfo(userInfo);
    return userInfo;
  };

  // 明示的にState更新を実施したい、というコード側の意図を表現するため別名をつけている
  const updateUserInfo = getUserInfo;

  const changeUserSettings = async (userSettings: {
    nickName: string
    birthDate: string /* ISO8601 日付 yyyy-MM-dd 形式 */
    displayR18: boolean
  }) => {
    const client = await cWebCatalogApi.createOpenapiFetch(fetchOption);
    if (!client) {
      return;
    }
    // これらを並列実行する動作速度上の理由はない
    await client.POST("/api/v2/UpdateNickname", {
      body: {
        nickname: userSettings.nickName
      }
    });
    await client.POST("/api/v2/UpdateBirthDate", {
      body: {
        birthDate: userSettings.birthDate
      }
    });
    await client.POST("/api/v2/UpdateDisplayR18", {
      body: {
        displayR18: userSettings.displayR18
      }
    });

    await updateUserInfo(); // 値を再取得して state に保存する
  };

  const promotePaymentDialog = usePromotePaymentDialogState();
  const checkPermission = async (viewmode: MainViewMode) => {
    if (!isLoginAttempted()) {
      // console.log("checkPermission updateUserInfo start. token:", state.get().value.token);
      await updateUserInfo();
      // console.log("checkPermission updateUserInfo end. token:", state.get().value.token);
    }
    if (!isLogin()) {
      // ログイン試行後 未ログインの場合は undefined を返す
      promotePaymentDialog.value = "noLogin";
      return undefined;
    }
    if (isAuthorizedRole(viewmode, state.get().value.userInfo.roles)) {
      // ログイン済みで 権限がある
      promotePaymentDialog.value = "authorized";
      return true;
    }
    // ログイン済みで 権限がない
    promotePaymentDialog.value = "showPromote";
    return false;
  };

  const getUserSnsLogins: () => Promise<IUserSnsAuth[] | undefined> = async () => {
    const client = await cWebCatalogApi.createOpenapiFetch(fetchOption);
    const response = await client?.GET("/api/v2/UserSnsLogins");
    return response?.data?.userSnsAuths;
  };

  /**
   * 決済開始リンクを表示するか
   */
  const canStartPayment = () => userInfo().roles.indexOf("Platinum") < 0;

  return {
    /**
     * 取得済みのユーザー情報
     */
    userInfo,
    /**
     * サーバーにアクセスしてユーザー情報を再取得する
     */
    getUserInfo,
    /**
     * サーバーにアクセスしてユーザー情報を再取得する
     */
    updateUserInfo,
    /**
     * 権限チェック
     * この画面の呼び出しタイミングでログインを試みていなければログイン試行をする
     * @param mode 表示ViewMode
     * @returns true = 権限有り, false = 権限無し(ログイン済み), undefined = 未ログイン
     */
    checkPermission,
    /**
     * ユーザー情報を更新
     */
    changeUserSettings,
    /**
     * 表示可能なRoleのうちの1つを返す
     */
    displayStatus,
    /**
     * ログインを試みていれば true
     */
    isLoginAttempted,
    /**
     * ログイン状態
     * @returns トークンが有効でなくても 直近有効なログインをしていれば True
     */
    isLogin,
    /**
     * 認可: サービスを使用できるステータスか
     */
    isAuthorization,
    /**
     * 成人であるか
     * circlems_r18: 1 かつ 誕生日換算で18歳以上
     */
    canDisplayR18,
    /**
     * 決済開始できるか を制御する
     */
    canStartPayment,
    clearUserState,
    setUserState,
    /**
     * トークン有効期限内であればトークンを返す。そうでなければ undefined を返す
     */
    getValidAuthToken,
    getUserSnsLogins,
    /**
     * WebAPIアクセスのためのトークンを取得する非同期関数
     */
    getAccessTokenWithLogin,
  };
};

export const getAuth2LogOutUrl = () => {
  const config = useCirclemsConfig().get();
  return `${config.circlemsConfig.CIRCLE_MS_AUTH2_BASE_URL}/account/logOutChild?returnUrl=${location.origin}`;
};

export const getAuth2LoginUrls = (returnUrl: string) => {
  const config = useCirclemsConfig().get();
  return {
    circlems: `${config.circlemsConfig.CIRCLE_MS_AUTH2_BASE_URL}?returnUrl=${returnUrl}`,
    google: `${config.circlemsConfig.CIRCLE_MS_AUTH2_BASE_URL}/account/externalLogIn?provider=https://accounts.google.com&returnUrl=${returnUrl}`
  };
};

/**
 * "/api/v2/UserInfo" の結果から undefined/null を含まないオブジェクトに変換する
 * 未ログイン状態で利用する nickname "Anonymous" をここで指定している
 */
const convertToUserInfo = (data: components["schemas"]["V2UserInfoItem"] | undefined): UserInfo => (
  {
    sub: data?.sub ?? "",
    nickname: data?.nickname ?? "Anonymous",
    email: data?.email ?? "",
    email_verified: data?.email_verified ?? false,
    updated_at: data?.updated_at ?? 0,
    circlems_r18: data?.circlems_r18 ?? 0,
    birthdate: data?.birthdate ? data?.birthdate : undefined,
    // この時点で有効なRoleのみに絞り込んでからStateに格納する
    roles: (<Role[]>data?.roles ?? []).filter(role => validRoleList.includes(role)),
  }
);

export const getAddExternalLoginUrl = (sns: IUserSnsAuth) => {
  if (sns.loginProvider) {
    const config = useCirclemsConfig().get();
    return `${config.circlemsConfig.CIRCLE_MS_AUTH2_BASE_URL}/Account/AddExternalLogin?provider=${sns.loginProvider}&returnUrl=${location.origin}`;
  }
  return undefined;
};

export const getRemoveExternalLoginUrl = (sns: IUserSnsAuth) => {
  if (sns.loginProvider) {
    const config = useCirclemsConfig().get();
    return `${config.circlemsConfig.CIRCLE_MS_AUTH2_BASE_URL}/Account/RemoveExternalLogin?provider=${sns.loginProvider}&returnUrl=${location.origin}`;
  }
  return undefined;
};
